import React, { RefObject, useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { MaintenanceChartPopover } from './components/MaintenanceChartPopover';
import {
  enrichTasks,
  getOption,
  organizeGanttTasks,
  updateTaskColors,
} from './utils';
import { Pagination } from './components/Pagination';
import { MaintenanceTask, MaintenanceTasks } from './types';
import { TASK_RECTANGLE_COLOR } from './const';
import { Legend } from './components/Legend';
import { useLazyGetMaintenanceDataQuery } from '@/store/api/maintenanceApi';
import { useParams } from 'react-router-dom';
import { Loading } from '@/components/shared/feedback/Loading';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { useActiveAssetsType } from '@/hooks/shared/useActiveAssetsType';
import { Skeleton } from '@/components/ui/skeleton';

enum POPOVER_HORIZONTAL_OFFSET {
  onDiagnostics = 300,
  onSensorFusion = 315,
}

enum POPOVER_VERTICAL_OFFSET {
  onDiagnostics = 140,
  onSensorFusion = 130,
}

interface MaintenanceChartProps {
  selectedComponent: any;
  xAxisMin?: string | undefined;
  xAxisMax?: string | undefined;
  context?: MAINTENANCE_CHART_CONTEXT;
  wrapperRef: RefObject<HTMLDivElement>;
  diagnosticsChartRef?: React.RefObject<ReactECharts>;
  maintenanceChartRef?: React.RefObject<ReactECharts>;
  hideLegend?: boolean;
}

export enum MAINTENANCE_CHART_CONTEXT {
  Diagnostics = 'diagnostics',
  SensorFusion = 'sensorFusion',
  SchemeSensors = 'schemeSensors',
}

export const MaintenanceChart = ({
  selectedComponent,
  xAxisMin,
  xAxisMax,
  context = MAINTENANCE_CHART_CONTEXT.Diagnostics,
  wrapperRef,
  diagnosticsChartRef,
  maintenanceChartRef,
  hideLegend = false,
}: MaintenanceChartProps) => {
  const { isMobileAsset } = useActiveAssetsType();
  const { siteId, id: machineId } = useParams<string>();
  const {
    dateRange: { from, to },
  } = useSelector((state: RootState) => state.assetsStudio);
  const [tasks, setTasks] = useState<MaintenanceTasks>([]);
  const [taskPages, setTaskPages] = useState<Array<Array<MaintenanceTask>>>([]);
  const [activeTaskPage, setActiveTaskPage] = useState<number>(0);
  const [selectedTask, setSelectedTask] = useState<MaintenanceTask>();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [option, setOption] = useState<any>();
  const [dataZoom, setDataZoom] = useState<Record<string, number>>({ start: 0, end: 100 });
  const isDiagnosticsContext = useMemo(() => context === MAINTENANCE_CHART_CONTEXT.Diagnostics, []);
  const [fetchMaintenanceData, { data: maintenanceData, isLoading: isLoadingMaintenanceData }] =
    useLazyGetMaintenanceDataQuery();

  const onEventsHandlers = {
    click: (params: any) => {
      const clickedTaskIndex = params.dataIndex;
      const wrapperBoundingRect = wrapperRef.current!.getBoundingClientRect();

      const x = isDiagnosticsContext
        ? params.event.event.clientX - wrapperBoundingRect.left - POPOVER_HORIZONTAL_OFFSET.onDiagnostics
        : params.event.event.clientX - wrapperBoundingRect.left - POPOVER_HORIZONTAL_OFFSET.onSensorFusion;

      const y = isDiagnosticsContext
        ? params.event.event.clientY -
          wrapperBoundingRect.top +
          wrapperBoundingRect.top -
          POPOVER_VERTICAL_OFFSET.onDiagnostics
        : params.event.event.clientY - wrapperBoundingRect.top - POPOVER_VERTICAL_OFFSET.onSensorFusion;

      setPopoverPosition({ x, y });
      setIsPopoverOpen(true);

      setSelectedTask(params.data);
      const componentId = typeof selectedComponent === 'object' ? selectedComponent?.component_id : selectedComponent;
      setTasks(updateTaskColors(tasks, clickedTaskIndex, componentId));
    },
    datazoom: (e: any) => {
      if (e.batch?.[0]) {
        const { start, end, startValue, endValue } = e.batch[0];
        const updatedZoom: Record<string, number> = {};

        if (start !== undefined && end !== undefined) {
          updatedZoom.start = start;
          updatedZoom.end = end;
        }

        if (startValue !== undefined && endValue !== undefined) {
          updatedZoom.startValue = startValue;
          updatedZoom.endValue = endValue;
        }

        if (Object.keys(updatedZoom).length) {
          setDataZoom(updatedZoom);
        }
      }
    },
    finished: () => {
      if (diagnosticsChartRef && maintenanceChartRef) {
        if (maintenanceChartRef.current && diagnosticsChartRef.current) {
          const maintenanceChartInstance = maintenanceChartRef.current.getEchartsInstance();
          const diagnosticsChartInstance = diagnosticsChartRef.current.getEchartsInstance();

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          echarts.connect([maintenanceChartInstance, diagnosticsChartInstance]);

          diagnosticsChartRef.current.getEchartsInstance().on('restore', (e: any) => {
            diagnosticsChartRef.current?.getEchartsInstance().dispatchAction({
              type: 'dataZoom',
              start: 0,
              end: 100,
            });

            setDataZoom({ start: 0, end: 100 });
          });
        }
      }
    },
    restore: () => setDataZoom({ start: 0, end: 100 }),
  };

  const onPopoverClose = () => {
    const componentId = typeof selectedComponent === 'object' ? selectedComponent?.component_id : selectedComponent;

    const updatedTasks = tasks.map((task: MaintenanceTask) => {
      const isSelectedComponent =
        componentId && (task.details?.componentId === componentId || task.details?.componentUiName === componentId);
      return {
        ...task,
        color: isSelectedComponent ? '#7071f3' : TASK_RECTANGLE_COLOR,
        borderColor: isSelectedComponent ? '#7071f3' : TASK_RECTANGLE_COLOR,
      };
    });

    setTasks(updatedTasks);
    setIsPopoverOpen(false);
    setSelectedTask(undefined);
  };

  const onPrevClick = () => {
    if (taskPages && activeTaskPage > 0 && taskPages[activeTaskPage - 1].length) {
      setTasks(taskPages[activeTaskPage - 1]);
      setActiveTaskPage((prevPage) => prevPage - 1);
    }
  };

  const onNextClick = () => {
    if (taskPages && activeTaskPage < taskPages.length - 1 && taskPages[activeTaskPage + 1].length) {
      setTasks(taskPages[activeTaskPage + 1]);
      setActiveTaskPage((prevPage) => prevPage + 1);
    }
  };

  useEffect(() => {
    if (maintenanceData) {
      // Handle both object and string formats for selectedComponent
      const componentId = typeof selectedComponent === 'object' ? selectedComponent?.component_id : selectedComponent;

      const processedTasks: MaintenanceTasks = enrichTasks(maintenanceData, componentId);

      if (xAxisMin && xAxisMax) {
        const organizedTasks = organizeGanttTasks(processedTasks, xAxisMin, xAxisMax);

        setTaskPages(organizedTasks);
        setTasks(organizedTasks.length ? organizedTasks[activeTaskPage] : []);
      }
    }
  }, [selectedComponent, maintenanceData, activeTaskPage, xAxisMin, xAxisMax]);

  useEffect(
    () => setOption(getOption(tasks, dataZoom, isDiagnosticsContext, xAxisMin, xAxisMax)),
    [tasks, dataZoom, xAxisMin, xAxisMax]
  );

  useEffect(() => {
    fetchMaintenanceData(
      {
        site_id: siteId,
        assetId: machineId,
        fromDate: from,
        toDate: to,
        assetCategory: isMobileAsset ? 'mobile' : 'fixed',
      },
      true
    );
  }, [machineId, siteId, from, to, isMobileAsset]);

  const chartElement = useMemo(() => {
    return (
      <ReactECharts
        ref={maintenanceChartRef}
        className="diagnostics-maintenance-chart"
        echarts={echarts}
        option={option}
        onEvents={onEventsHandlers}
        style={{ height: '40px', width: '100%' }}
      />
    );
  }, [option]);

  const noDataPlaceholderElement = useMemo(() => {
    return (
      <div className="text-[0.875rem] text-[#55687D] p-4">
        {maintenanceData?.length ? (
          <div>
            <p>No maintenance data was recorded during the selected diagnostic period.</p>
            <p>
              Please check the &apos;Maintenance Data&apos; tab to see if relevant data is available for that timeframe.
            </p>
          </div>
        ) : (
          <p>No maintenance data available for the selected date range.</p>
        )}
      </div>
    );
  }, [maintenanceData]);

  const hasData = Boolean(tasks?.length && option);

  return (
    <div className="px-4">
      <div className={hasData ? 'dm-bg-tertiary rounded-lg' : ''}>
        {isLoadingMaintenanceData ? (
          <div className="relative w-full h-[45px] rounded-md dm-bg-secondary animate-pulse flex items-center ">
            <div className="flex items-center justify-center min-h-0 w-full space-x-1">
              <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce [animation-delay:0ms]" />
              <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce [animation-delay:150ms]" />
              <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce [animation-delay:300ms]" />
            </div>
          </div>
        ) : (
          <div>
            {hasData ? (
              <div className="flex justify-between gap-0">
                {chartElement}

                <MaintenanceChartPopover
                  isPopoverOpen={isPopoverOpen}
                  onPopoverClose={onPopoverClose}
                  popoverPosition={popoverPosition}
                  selectedTask={selectedTask}
                />

                {taskPages.length > 1 && (
                  <Pagination
                    onPrevClick={onPrevClick}
                    onNextClick={onNextClick}
                    taskPages={taskPages}
                    activeTaskPageIndex={activeTaskPage}
                    lastTaskPageIndex={taskPages.length}
                  />
                )}
              </div>
            ) : (
              noDataPlaceholderElement
            )}
          </div>
        )}
      </div>

      {!hideLegend && hasData && <Legend items={[{ color: '#01152B', label: 'Maintenance Task' }]} />}
    </div>
  );
};
